import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'dragonfit_theme_v1';

export const DEFAULT_PRIMARY = '#22c55e';

export const DEFAULT_THEME = Object.freeze({
  primary: DEFAULT_PRIMARY,
  backgroundImage: null,
  backgroundOpacity: 0.55,
});

// Built-in palette presets. The `nameKey` resolves through i18n.
export const PALETTE_PRESETS = [
  { id: 'green',  nameKey: 'customize.preset_green',  color: '#22c55e' },
  { id: 'blue',   nameKey: 'customize.preset_blue',   color: '#3b82f6' },
  { id: 'cyan',   nameKey: 'customize.preset_cyan',   color: '#06b6d4' },
  { id: 'orange', nameKey: 'customize.preset_orange', color: '#f97316' },
  { id: 'pink',   nameKey: 'customize.preset_pink',   color: '#ec4899' },
  { id: 'purple', nameKey: 'customize.preset_purple', color: '#a855f7' },
  { id: 'red',    nameKey: 'customize.preset_red',    color: '#ef4444' },
  { id: 'gold',   nameKey: 'customize.preset_gold',   color: '#eab308' },
];

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || '').trim());
  if (!m) return [34, 197, 94];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
};

// Pick black/white for text painted on top of `primary` based on perceived luminance.
const contrastForeground = ([r, g, b]) => {
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#000000' : '#ffffff';
};

const clamp01 = (n) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));

const applyToRoot = (theme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const rgb = hexToRgb(theme.primary || DEFAULT_PRIMARY);

  root.style.setProperty('--primary', theme.primary || DEFAULT_PRIMARY);
  root.style.setProperty('--primary-rgb', rgb.join(', '));
  root.style.setProperty('--primary-foreground', contrastForeground(rgb));
  root.style.setProperty('--ring', theme.primary || DEFAULT_PRIMARY);
  root.style.setProperty(
    '--bg-overlay',
    `rgba(0, 0, 0, ${clamp01(theme.backgroundOpacity ?? DEFAULT_THEME.backgroundOpacity)})`
  );

  if (theme.backgroundImage) {
    root.style.setProperty('--bg-image', `url("${theme.backgroundImage}")`);
    root.classList.add('has-bg-image');
  } else {
    root.style.removeProperty('--bg-image');
    root.classList.remove('has-bg-image');
  }
};

const loadTheme = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_THEME };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_THEME, ...parsed };
  } catch {
    return { ...DEFAULT_THEME };
  }
};

const saveTheme = (theme) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
    return true;
  } catch {
    // QuotaExceededError or serialization failure (huge background image).
    return false;
  }
};

// Apply persisted theme synchronously on module load to avoid FOUC.
if (typeof document !== 'undefined') {
  applyToRoot(loadTheme());
}

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(loadTheme);
  const [quotaError, setQuotaError] = useState(false);

  useEffect(() => { applyToRoot(theme); }, [theme]);

  const updateTheme = useCallback((patch) => {
    setTheme((prev) => {
      const next = { ...prev, ...patch };
      const ok = saveTheme(next);
      setQuotaError(!ok);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    saveTheme(DEFAULT_THEME);
    setQuotaError(false);
    setTheme({ ...DEFAULT_THEME });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, reset, presets: PALETTE_PRESETS, quotaError }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
